import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { deviceMin } from '../../theme/breakpoints';
import { spacing } from '../../theme/spacing';

/* 1.  Breakpoints issus du thème */
const breakpointCss = {
  mobileL: deviceMin.mobileL,
  tablet: deviceMin.tablet,
  laptop: deviceMin.laptop,
  laptopL: deviceMin.laptopL,
};

/* 2.  Helpers génériques  ---------------------------------------------- */

/* grid-template-columns : prend en charge auto-fit + minmax */
const makeColTemplate = (cols) => {
  if (typeof cols === 'number') return `repeat(${cols}, 1fr)`;
  if (cols && typeof cols === 'object' && 'fit' in cols) {
    const min = typeof cols.fit === 'number' ? `${cols.fit}px` : cols.fit;
    return `repeat(auto-fit, minmax(${min}, 1fr))`;
  }
  if (typeof cols === 'string') return cols;          // fallback avancé
  return '';
};

/* grid-template-rows : simple repeat */
const makeRowTemplate = (rows) =>
  typeof rows === 'number' ? `repeat(${rows}, 1fr)` : '';

/* gap : mappe vers les tokens spacing + cas booléen */
const makeGapValue = (val) => {
  if (val === true) return spacing[24];               // prop sans valeur → token 24

  if (typeof val === 'number') {
    if (val in spacing) return spacing[val];          // token known
    return val;                                       // valeur brute (ex : 15)
  }

  if (typeof val === 'string') {
    if (val in spacing) return spacing[val];          // '24'
    return val;                                       // '2rem'
  }

  return '';
};

/* Génère les règles pour chaque breakpoint */
const buildAxisStyles = (axis, obj, maker) =>
  Object.entries(obj).map(([bp, value]) => {
    if (value === undefined || value === null || value === false) return '';

    const template = maker(value);
    if (!template) return '';

    if (bp === 'base') {
      return css`
        ${axis}: ${template};
      `;
    }

    if (!breakpointCss[bp]) return '';

    return css`
      @media ${breakpointCss[bp]} {
        ${axis}: ${template};
      }
    `;
  });

/* 3.  Styled-component --------------------------------------------------- */
const StyledGridContainer = styled.div`
  display: grid;

  /* Colonnes */
  ${({ $columns }) =>
    $columns && buildAxisStyles('grid-template-columns', $columns, makeColTemplate)}

  /* Lignes */
  ${({ $rows }) =>
    $rows && buildAxisStyles('grid-template-rows', $rows, makeRowTemplate)}

  /* Gap */
  ${({ $gap }) => $gap && buildAxisStyles('gap', $gap, makeGapValue)}
`;

/* 4.  Composant React ---------------------------------------------------- */
const GridContainer = ({ as: Tag, id, className, children, columns, rows, gap }) => {
  /* Colonnes : si nombre | {fit:…} → base */
  const colIsBaseLike =
    typeof columns === 'number' ||
    (columns && typeof columns === 'object' && 'fit' in columns);
  const colObj = colIsBaseLike ? { base: columns } : columns;

  /* Lignes : si nombre → base */
  const rowObj = typeof rows === 'number' ? { base: rows } : rows;

  /* Gap : absent = aucun; bool/valeur simple = base; objet = responsive */
  const gapObj =
    gap === undefined
      ? undefined
      : typeof gap === 'object' && !Array.isArray(gap)
        ? gap
        : { base: gap };

  return (
    <StyledGridContainer
      as={Tag}
      id={id}
      className={className}
      $columns={colObj}
      $rows={rowObj}
      $gap={gapObj}
    >
      {children}
    </StyledGridContainer>
  );
};

/* 5.  PropTypes ---------------------------------------------------------- */
const fitShape = PropTypes.shape({
  fit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
});

GridContainer.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  /** Colonnes : number | {fit:min} | objet par breakpoint | string brute */
  columns: PropTypes.oneOfType([
    PropTypes.number,
    fitShape,
    PropTypes.string,
    PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.number, fitShape, PropTypes.string]),
    ),
  ]),
  /** Lignes : number | objet par breakpoint */
  rows: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.objectOf(PropTypes.number),
  ]),
  /** Gap : bool (token 24) | number | string | objet responsive */
  gap: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
    PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
    ),
  ]),
  /** Balise HTML ou composant à rendre */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  children: PropTypes.node.isRequired,
};

GridContainer.defaultProps = {
  id: undefined,
  className: '',
  columns: undefined,
  rows: undefined,
  gap: undefined,
  as: 'div',
};

export default GridContainer;
